import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronExpandYOutline18 as ChevronsUpDown,
	IconSquareArrowUpRightOutline18 as OpenIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { useZeroWorkspace } from "@/lib/zero"
import { portalPublicUrl } from "@/screens/settings/portal-instance-form"

interface Props {
	activeId: string | null
	onChange: (id: string | null) => void
	/** Render variant. `minimal` matches the docs header style (no border, plain text). */
	variant?: "minimal" | "outlined"
}

/** Synthetic option representing the unprefixed root domain. */
const ROOT_OPTION = { id: null as string | null, name: "Main portal", slug: "" }

/**
 * Top-level picker shown on Changelog and Docs list views. Returns `null`
 * unless the workspace has at least one extra PortalInstance — Root alone is
 * single-portal mode and needs no UI.
 *
 * Single-select. Picking an option switches the dashboard's portal context and
 * defaults newly-created items to that portal. `null` represents the Root
 * (unprefixed) portal.
 */
export function PortalInstanceSwitcher({
	activeId,
	onChange,
	variant = "minimal",
}: Props) {
	const workspace = useZeroWorkspace()
	const [isOpen, setIsOpen] = useState(false)
	const extras = workspace?.portalInstances ?? []

	// Multi-portal UI activates once the workspace has 1+ extra portals beyond
	// the implicit Root. With only Root there's nothing to switch between.
	if (extras.length < 1) return null

	const hasNamedDefault = extras.some((i) => i.isDefault)
	const options = [
		{
			...ROOT_OPTION,
			url: portalPublicUrl(workspace ?? {}, null, hasNamedDefault),
		},
		...extras.map((i) => ({
			id: i.id,
			name: i.name,
			slug: i.slug,
			url: portalPublicUrl(workspace ?? {}, i, hasNamedDefault),
		})),
	]
	const active = options.find((o) => o.id === activeId) ?? ROOT_OPTION

	// `max-w-[14rem]` caps the trigger so long portal names don't push the
	// surrounding layout. `min-w-0` lets the truncating span shrink inside the
	// flex parent.
	const triggerClass =
		variant === "minimal"
			? "-ml-1.5 flex max-w-[14rem] min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
			: "-ml-2.5 flex max-w-[14rem] min-w-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-850 dark:hover:bg-gray-800"

	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			content={
				// The highlight opens on the current portal rather than the first row.
				<Command defaultValue={`${active.slug} ${active.name}`}>
					<CommandInput placeholder="Switch portal..." showIcon />
					<CommandList>
						<CommandEmpty>No portals.</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.id ?? "__root__"}
									value={`${option.slug} ${option.name}`}
									onSelect={() => {
										onChange(option.id)
										setIsOpen(false)
									}}
								>
									<div className="flex w-full min-w-0 items-center gap-2">
										<span
											className={cn(
												"min-w-0 flex-1 truncate text-sm",
												option.id === active.id && "font-medium text-primary",
											)}
											title={option.name}
										>
											{option.name}
										</span>
										{option.id === active.id && (
											<Check
												aria-label="Selected"
												className="h-3.5 w-3.5 flex-none text-secondary"
											/>
										)}
										<IconButton
											variant="dark-ghost"
											size="md"
											Icon={OpenIcon}
											aria-label={`Open ${option.name} in a new tab`}
											title="Open in a new tab"
											className="-my-1 -mr-1 flex-none rounded-md opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-aria-selected:opacity-100"
											onPointerDown={(e) => e.stopPropagation()}
											onClick={(e) => {
												e.stopPropagation()
												window.open(option.url, "_blank", "noopener")
											}}
										/>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
			className="min-w-[240px] p-0"
			align="start"
		>
			<button type="button" className={triggerClass} title={active.name}>
				<span className="min-w-0 truncate">{active.name}</span>
				<ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 text-tertiary" />
			</button>
		</Popover>
	)
}
